'use client';

import { AnimatePresence } from 'framer-motion';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { ForecastDay } from '../../types/weatherApi';
import DailyForecastCard from './DailyForecastCard';
import HourlyForecastModal from './HourlyForecastModal';

const TABS = [
  { label: 'На 3 дня', days: 3 },
  { label: 'На 7 дней', days: 7 },
  { label: 'На 10 дней', days: 10 },
];

function ForecastTabs({ forecast }: { forecast: ForecastDay[] }) {
  const [selectedDays, setSelectedDays] = useState(3);
  const [modalData, setModalData] = useState<ForecastDay | null>(null);

  const visibleForecast = useMemo(() => {
    return (forecast ?? []).slice(0, selectedDays);
  }, [forecast, selectedDays]);

  // Базовый lookup по date (для мгновенного открытия)
  const forecastByDate = useMemo(() => {
    const m = new Map<string, ForecastDay>();
    for (const d of forecast ?? []) m.set(d.date, d);
    return m;
  }, [forecast]);

  // --- Prefetch cache (L1 memory) + single-flight ---
  const detailsCacheRef = useRef(new Map<string, ForecastDay>());
  const inflightRef = useRef(new Map<string, Promise<ForecastDay | null>>());
  const hoverTimersRef = useRef(new Map<string, number>());

  const fetchDayDetails = useCallback(async (date: string): Promise<ForecastDay | null> => {
    // 1) memory cache
    const cached = detailsCacheRef.current.get(date);
    if (cached) return cached;

    // 2) single-flight per date
    const inflight = inflightRef.current.get(date);
    if (inflight) return inflight;

    const p = (async () => {
      try {
        const res = await fetch(`/api/forecast/day?date=${encodeURIComponent(date)}`, {
          cache: 'no-store',
        });
        if (!res.ok) return null;

        const data = (await res.json()) as ForecastDay | { message?: string };
        if (!data || typeof (data as any).date !== 'string') return null;

        detailsCacheRef.current.set(date, data as ForecastDay);
        return data as ForecastDay;
      } catch {
        return null;
      } finally {
        inflightRef.current.delete(date);
      }
    })();

    inflightRef.current.set(date, p);
    return p;
  }, []);

  const prefetchDay = useCallback(
    (date: string) => {
      if (detailsCacheRef.current.has(date) || inflightRef.current.has(date)) return;

      const existing = hoverTimersRef.current.get(date);
      if (existing) window.clearTimeout(existing);

      const t = window.setTimeout(() => {
        void fetchDayDetails(date);
        hoverTimersRef.current.delete(date);
      }, 160);

      hoverTimersRef.current.set(date, t);
    },
    [fetchDayDetails],
  );

  const cancelPrefetch = useCallback((date: string) => {
    const t = hoverTimersRef.current.get(date);
    if (t) {
      window.clearTimeout(t);
      hoverTimersRef.current.delete(date);
    }
  }, []);

  const handleCloseModal = useCallback(() => setModalData(null), []);

  const openModalForDate = useCallback(
    async (date: string) => {
      // если уже есть детальная версия — открываем её
      const cached = detailsCacheRef.current.get(date);
      if (cached) {
        setModalData(cached);
        return;
      }

      // иначе открываем базовую (скорее всего hour=[]), чтобы UI не тормозил
      const base = forecastByDate.get(date);
      if (!base) return;

      setModalData(base);

      // догружаем почасовку (если нужно)
      const details = await fetchDayDetails(date);
      if (details) setModalData(details);
    },
    [fetchDayDetails, forecastByDate],
  );

  const handleGridClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement;
      const cardEl = target.closest<HTMLElement>('[data-date]');
      if (!cardEl) return;

      const date = cardEl.dataset.date;
      if (!date) return;

      void openModalForDate(date);
    },
    [openModalForDate],
  );

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      const target = e.target as HTMLElement;
      const cardEl = target.closest<HTMLElement>('[data-date]');
      if (!cardEl) return;

      e.preventDefault();

      const date = cardEl.dataset.date;
      if (!date) return;

      void openModalForDate(date);
    },
    [openModalForDate],
  );

  return (
    <section className="text-white" aria-labelledby="forecast-tabs-title">
      <div className="flex items-center justify-between mb-6">
        <h2 id="forecast-tabs-title" className="text-2xl text-main font-semibold">
          Прогноз
        </h2>

        <div className="flex gap-2 md:gap-4" role="tablist" aria-label="Прогноз по дням">
          {TABS.map((tab) => (
            <button
              key={tab.days}
              onClick={() => setSelectedDays(tab.days)}
              role="tab"
              aria-selected={selectedDays === tab.days}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl transition-all duration-300 cursor-pointer text-sm md:text-base ${
                selectedDays === tab.days
                  ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg scale-105'
                  : 'bg-sky-700 text-sky-200 hover:bg-sky-600'
              }`}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        onClick={handleGridClick}
        onKeyDown={handleGridKeyDown}
      >
        {visibleForecast.map((day) => (
          <div
            key={day.date}
            data-date={day.date}
            role="button"
            tabIndex={0}
            className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 rounded-xl"
            aria-label={`Открыть почасовой прогноз на ${day.date}`}
            onMouseEnter={() => prefetchDay(day.date)}
            onMouseLeave={() => cancelPrefetch(day.date)}
            onFocus={() => prefetchDay(day.date)}
            onBlur={() => cancelPrefetch(day.date)}
          >
            <DailyForecastCard dayForecast={day} />
          </div>
        ))}
      </div>

      <AnimatePresence>
        {modalData && <HourlyForecastModal dayForecast={modalData} onClose={handleCloseModal} />}
      </AnimatePresence>
    </section>
  );
}

export default memo(ForecastTabs);
